import api from "@/api";
import { matchesSearch } from "@/shared/utils/normalizeText";
import {
  absoluteUrl,
  colunaDeVerdade,
  defineTool,
  jsonResult,
  postDeVerdade,
  toCatalogItem,
  urlDaColuna,
  urlDoPost,
  type WebMcpToolsContext,
} from "./shared";

/**
 * Tools de catálogo e navegação — o núcleo, registrado globalmente.
 * Todas são de leitura ou navegação: nenhuma altera dados no servidor.
 */

/** Seções do catálogo → endpoints e rota de destino de cada item. */
const SECTIONS = {
  models: {
    list: "v1/modelos/getAllModels",
    categories: "v1/modelos/categories",
    route: "language-modelos-name-photo",
  },
  videos: {
    list: "v1/modelos/getAllModelsVideos",
    categories: "v1/modelos/videos/categories",
    route: "language-modelos-name-video",
  },
  "making-of": {
    list: "v1/modelos/getMakingOfModels",
    categories: "v1/modelos/making-of/categories",
    route: "language-modelos-name-making-of",
  },
  lives: {
    list: "v1/modelos/getLiveModels",
    categories: "v1/modelos/lives/categories",
    route: "language-modelos-name-live",
  },
} as const;

type SectionKey = keyof typeof SECTIONS;

const CATALOG_SECTIONS = [...Object.keys(SECTIONS), "columns", "blog"];

/** Destinos do open_page → nomes de rota do Nuxt (nunca URL montada à mão). */
const DESTINATIONS: Record<string, string> = {
  home: "language",
  models: "language-modelos",
  videos: "language-videos",
  "making-of": "language-making-of",
  lives: "language-lives",
  columns: "language-colunas",
  blog: "language-blog",
  ecards: "language-ecards",
  freetour: "language-freetour",
  bellacloud: "language-bellacloud",
  about: "language-sobre",
  contact: "language-contato",
  press: "language-imprensa",
  subscribe: "language-assine",
  login: "language-login",
  "my-account": "language-minhaconta",
  "my-credits": "language-me-credits",
  palpitao: "language-palpitao",
  "area-gold": "language-area-gold",
};

const MODEL_SECTIONS: Record<string, string> = {
  photo: "language-modelos-name-photo",
  video: "language-modelos-name-video",
  "making-of": "language-modelos-name-making-of",
  live: "language-modelos-name-live",
  perfil: "language-modelos-name-perfil",
  wallpaper: "language-modelos-name-wallpaper",
};

/**
 * `getAllModelsSelect` devolve só `{ value, label }` — sem slug, que é o que
 * monta a URL. O slug vem de `getModelSelect/{id}`, então memoizamos por id
 * para uma segunda busca pelo mesmo nome não bater na API de novo.
 */
const slugCache = new Map<number, { title: string; slug: string }>();

async function resolveModelSlug(
  id: number,
  language: string,
  signal: AbortSignal
): Promise<{ title: string; slug: string } | null> {
  const cached = slugCache.get(id);
  if (cached) return cached;

  const { data } = await api.get(
    `v1/modelos/getModelSelect/${id}?language=${language}`,
    { signal }
  );
  const model = data?.modelsArray?.[0];
  if (!model?.slug) return null;

  const resolved = { title: String(model.title ?? ""), slug: String(model.slug) };
  slugCache.set(id, resolved);
  return resolved;
}

/** `articletag` da API para "todas as colunas" (o mesmo de utils/colunasCategoria.ts). */
const TODAS_AS_COLUNAS = "9999";

type CategoriaDeColuna = { id: number; name: string; slug: string };

/** Categorias de colunas por idioma. Mudam raramente: uma busca por documento basta. */
const categoriasDeColunas = new Map<string, CategoriaDeColuna[]>();

async function listarCategoriasDeColunas(
  language: string,
  signal: AbortSignal
): Promise<CategoriaDeColuna[]> {
  const cached = categoriasDeColunas.get(language);
  if (cached) return cached;

  // `limit` corta os cards de cada categoria, não as categorias: com 1 a
  // resposta fica leve. O primeiro item é o "Recentes" (todas), que sai.
  const { data } = await api.get(
    `v1/home/getColumns?language=${language}&limit=1&mobile=false`,
    { signal }
  );
  const categorias: CategoriaDeColuna[] = (data?.columns ?? [])
    .filter((c: any) => String(c?.id) !== TODAS_AS_COLUNAS && c?.slugCategory)
    .map((c: any) => ({
      id: Number(c.id),
      name: String(c.category ?? ""),
      slug: String(c.slugCategory),
    }));
  if (categorias.length) categoriasDeColunas.set(language, categorias);
  return categorias;
}

export function catalogTools(ctx: WebMcpToolsContext): WebMcpToolDescriptor[] {
  const { router, language, t } = ctx;
  const text = t.tools;

  // Stores resolvidos AQUI, na factory, que roda dentro do contexto do Nuxt.
  // Dentro do `execute` não há contexto (quem chama é o browser, fora do ciclo
  // do app), então `useNuxtApp()` — e tudo que depende dele — estouraria.
  const storeLocale = localeStore();

  return [
    defineTool(t, {
      name: "search_models",
      description: text.search_models.description,
      annotations: { readOnlyHint: true },
      inputSchema: {
        type: "object",
        properties: {
          query: { type: "string", description: text.search_models.params.query },
          limit: {
            type: "number",
            minimum: 1,
            maximum: 10,
            description: text.search_models.params.limit,
          },
        },
        required: ["query"],
      },
      execute: async ({ query, limit }, { signal }) => {
        const { data } = await api.get(
          `v1/modelos/getAllModelsSelect?language=${language}`,
          { signal }
        );
        const all: Array<{ label: string; value: number }> =
          data?.modelsArray ?? [];

        // Mesma comparação da busca da página /modelos (ModelSearch.vue):
        // ignora acento, caixa e ordem das palavras.
        // Teto de 10: cada resultado custa uma chamada a getModelSelect para
        // descobrir o slug, e elas saem em paralelo.
        const matches = all
          .filter((m) => matchesSearch(String(m.label ?? ""), String(query ?? "")))
          .slice(0, Math.min(Math.max(Number(limit) || 8, 1), 10));

        if (!matches.length) return t.messages.noResults;

        const resolved = await Promise.all(
          matches.map((m) => resolveModelSlug(Number(m.value), language, signal))
        );

        const results = resolved
          .filter((m): m is { title: string; slug: string } => m !== null)
          .map((m) => ({
            name: m.title,
            slug: m.slug,
            url: absoluteUrl(router, {
              name: MODEL_SECTIONS.photo,
              params: { language, name: m.slug },
            }),
          }));

        return results.length ? jsonResult(results) : t.messages.noResults;
      },
    }),

    defineTool(t, {
      name: "browse_catalog",
      description: text.browse_catalog.description,
      annotations: { readOnlyHint: true },
      inputSchema: {
        type: "object",
        properties: {
          section: {
            type: "string",
            enum: CATALOG_SECTIONS,
            description: text.browse_catalog.params.section,
          },
          category: {
            type: "string",
            description: text.browse_catalog.params.category,
          },
          query: {
            type: "string",
            description: text.browse_catalog.params.query,
          },
          page: {
            type: "number",
            minimum: 1,
            description: text.browse_catalog.params.page,
          },
        },
        required: ["section"],
      },
      execute: async ({ section, category, query, page }, { signal }) => {
        const pageNumber = Math.max(Number(page) || 1, 1);
        // A mesma busca das caixas de pesquisa de /blog e /colunas: o termo vai
        // no `search` dos mesmos endpoints da listagem.
        const busca = String(query ?? "").trim();
        const search = encodeURIComponent(busca);

        // Blog e colunas não têm paginação na API: os dois endpoints só aceitam
        // `limit` (é assim que o "ver mais" das páginas funciona). Pedimos até o
        // fim da página desejada e cortamos o começo — mesmo comportamento que a
        // página já tem, sem inventar um parâmetro que o backend ignoraria. Os
        // slots de banner das duas listas saem no filtro (ver shared.ts).
        if (section === "blog") {
          const { data } = await api.get(
            `v1/blog/getBlogs?search=${search}&language=${language}&limit=${pageNumber * 12}&mobile=false`,
            { signal }
          );
          const posts = (data?.blogs ?? [])
            .slice((pageNumber - 1) * 12)
            .filter(postDeVerdade);
          if (!posts.length) return t.messages.noResults;
          return jsonResult(
            posts.map((post: any) => ({
              title: post.title ?? "",
              date: post.date ?? null,
              url: urlDoPost(router, language, post),
            }))
          );
        }

        if (section === "columns") {
          // A API filtra por id da categoria (`articletag`); o agente recebe o
          // slug em list_categories, então traduzimos aqui.
          let articletag = "";
          if (category) {
            const alvo = String(category);
            const categorias = await listarCategoriasDeColunas(language, signal);
            const categoria = categorias.find(
              (c) => c.slug === alvo || String(c.id) === alvo
            );
            if (!categoria) return t.messages.noResults;
            articletag = String(categoria.id);
          }

          const { data } = await api.get(
            `v1/colunas/getColumns?search=${search}&articletag=${articletag}&language=${language}&limit=${pageNumber * 12}&mobile=false`,
            { signal }
          );
          const posts = (data?.postsColumns ?? [])
            .slice((pageNumber - 1) * 12)
            .filter(colunaDeVerdade);
          if (!posts.length) return t.messages.noResults;
          return jsonResult(
            posts.map((post: any) => ({
              title: post.title ?? "",
              date: post.date ?? null,
              url: urlDaColuna(router, language, post),
            }))
          );
        }

        const config = SECTIONS[section as SectionKey];
        if (!config) return t.messages.unknownSection;
        if (busca) return t.messages.searchOnlyArticles;

        const url = category
          ? `${config.categories}/${encodeURIComponent(String(category))}?language=${language}&order=order&page=${pageNumber}`
          : `${config.list}?order=order&language=${language}&page=${pageNumber}`;

        const { data } = await api.get(url, { signal });
        const paginated = data?.modelsArray;
        const items = paginated?.data ?? [];
        if (!items.length) return t.messages.noResults;

        return jsonResult({
          page: paginated?.current_page ?? pageNumber,
          lastPage: paginated?.last_page ?? null,
          total: paginated?.total ?? null,
          items: items.map((item: any) =>
            toCatalogItem(router, language, config.route, item)
          ),
        });
      },
    }),

    defineTool(t, {
      name: "list_categories",
      description: text.list_categories.description,
      annotations: { readOnlyHint: true },
      inputSchema: {
        type: "object",
        properties: {
          section: {
            type: "string",
            enum: [...Object.keys(SECTIONS), "columns"],
            description: text.list_categories.params.section,
          },
        },
        required: ["section"],
      },
      execute: async ({ section }, { signal }) => {
        if (section === "columns") {
          const categorias = await listarCategoriasDeColunas(language, signal);
          if (!categorias.length) return t.messages.noResults;
          return jsonResult(categorias.map(({ name, slug }) => ({ name, slug })));
        }

        const config = SECTIONS[section as SectionKey];
        if (!config) return t.messages.unknownSection;

        const { data } = await api.get(
          `${config.categories}?language=${language}`,
          { signal }
        );
        const categories = data?.categories ?? [];
        if (!categories.length) return t.messages.noResults;

        return jsonResult(
          categories.map((c: any) => ({
            name: c.name,
            slug: c.slug,
            count: c.modelCount ?? c.videoCount ?? c.count ?? null,
          }))
        );
      },
    }),

    defineTool(t, {
      name: "open_page",
      description: text.open_page.description,
      inputSchema: {
        type: "object",
        properties: {
          destination: {
            type: "string",
            enum: [...Object.keys(DESTINATIONS), "model"],
            description: text.open_page.params.destination,
          },
          model_slug: {
            type: "string",
            description: text.open_page.params.model_slug,
          },
          model_section: {
            type: "string",
            enum: Object.keys(MODEL_SECTIONS),
            description: text.open_page.params.model_section,
          },
        },
        required: ["destination"],
      },
      execute: async ({ destination, model_slug, model_section }) => {
        // Reporta SEMPRE a rota onde a navegação realmente parou, e não a
        // pretendida: destinos como my-account e my-credits passam pelo
        // middleware check-login e aterrissam em /login. Anunciar a URL de
        // destino faria o agente afirmar que abriu a conta estando no login.
        const ondeParou = () =>
          window.location.origin + router.currentRoute.value.fullPath;

        if (destination === "model") {
          if (!model_slug) return t.messages.modelNotFound;
          const routeName =
            MODEL_SECTIONS[String(model_section ?? "photo")] ??
            MODEL_SECTIONS.photo;
          await router.push({
            name: routeName,
            params: { language, name: String(model_slug) },
          });
          return `${t.messages.navigated} ${ondeParou()}`;
        }

        const routeName = DESTINATIONS[String(destination)];
        if (!routeName) return t.messages.unknownDestination;

        await router.push({ name: routeName, params: { language } });
        return `${t.messages.navigated} ${ondeParou()}`;
      },
    }),

    defineTool(t, {
      name: "set_language",
      description: text.set_language.description,
      inputSchema: {
        type: "object",
        properties: {
          language: {
            type: "string",
            enum: ["pt-br", "en"],
            description: text.set_language.params.language,
          },
        },
        required: ["language"],
      },
      execute: async ({ language: target }) => {
        const next = String(target) === "en" ? "en" : "pt-br";
        const current = router.currentRoute.value;

        // Troca só o parâmetro `language` da rota atual. O Header faz isso com
        // um replace de string no fullPath; por nome de rota é determinístico e
        // preserva query/hash.
        storeLocale.setLocale(next);

        // A navegação sai num macrotask, DEPOIS que esta promise resolver.
        // Motivo: trocar o idioma dispara o watch do useWebMcpTools, que faz
        // controller.abort() — e esse mesmo signal foi passado ao registerTool,
        // então o browser cancelava a execução DESTA tool antes de ela
        // responder. O efeito colateral acontecia, mas o agente recebia
        // "UnknownError: The operation failed..." e tendia a repetir a chamada,
        // fazendo o idioma pingar de volta. Resolvendo primeiro, o retorno
        // chega ao agente e só então o re-registro acontece.
        setTimeout(() => {
          void router.push({
            name: current.name ?? "language",
            params: { ...current.params, language: next },
            query: current.query,
            hash: current.hash,
          });
        }, 0);

        return `${t.messages.languageChanged} ${next}.`;
      },
    }),
  ];
}
